import { motion } from 'framer-motion';
import { Search, Filter, ArrowUpDown, MoreVertical } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShellV2 from '@/components/PortalShellV2';
import AutoCursor from '@/components/AutoCursor';

const facilities = [
  { name: 'Burjeel-AlNahyan',   type: 'Hospital', location: 'Abu Dhabi', localNumber: '—', status: 'Active' },
  { name: 'Burjeel - Musaffah', type: 'Hospital', location: 'Abu Dhabi', localNumber: '—', status: 'Active' },
];

const tabs = [
  { label: 'All',      count: 2, active: true },
  { label: 'Active',   count: 2 },
  { label: 'Inactive', count: 0 },
];

export default function FacilitiesListV2Scene({ onNext }) {
  const cursorPath = [
    { x: 280, y: 70,  delay: 1.0 },
    { x: 280, y: 70,  delay: 2.2 },
    { x: 320, y: 480, delay: 2.0 },              // hover over Burjeel-AlNahyan row
    { x: 320, y: 480, delay: 1.2, click: true }, // click to open detail
  ];

  return (
    <PortalShellV2
      variant="sidebar"
      menu="lite"
      activeGroup="Manage"
      activeSub="Facility"
      overlay={<AutoCursor targets={cursorPath} startDelay={0.4} />}
    >
      <div data-testid="scene-facilities-list-v2" className="px-8 pt-6 pb-4">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="font-heading font-extrabold text-[34px] leading-none text-[#0A0A0A] flex items-center gap-2">
                Facilities
                <InfoSpot
                  text="Facilities are the physical locations in your organisation — hospitals, clinics, pharmacies. Each facility has its own users, contracts and operational details."
                  position="bottom"
                  autoOpenAt={1.0}
                  autoCloseAfter={2.5}
                  glowAt={0.7}
                />
              </h1>
              <p className="mt-2 font-body text-[13px] text-[#6B7280]">Manage facilities and their operational details</p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-0 rounded-xl bg-white border border-[#E5E7EB] p-1">
              {tabs.map(tab => (
                <div key={tab.label} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] font-body cursor-pointer ${tab.active ? 'bg-white text-[#0A0A0A] font-semibold shadow-sm border border-[#E5E7EB]' : 'text-[#6B7280]'}`}>
                  {tab.label}
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${tab.active ? 'bg-teal-600 text-white' : 'bg-[#E5E7EB] text-[#6B7280]'}`}>{tab.count}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center"><Search size={14} className="text-[#6B7280]" /></button>
              <button className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center"><Filter size={14} className="text-[#6B7280]" /></button>
            </div>
          </div>
        </motion.div>

        <div className="rounded-xl border border-[#E5E7EB] overflow-hidden bg-white">
          <table className="w-full">
            <thead>
              <tr className="bg-[#FAFAFA] border-b border-[#E5E7EB]">
                {['Facility name', 'Type', 'Location', 'Local number', 'Status'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-[12px] font-body font-semibold text-[#374151]">
                    <span className="inline-flex items-center gap-1">{h}<ArrowUpDown size={11} className="text-[#9CA3AF]" /></span>
                  </th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <motion.tr
                  key={f.name}
                  initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.08 }}
                  onClick={i === 0 ? onNext : undefined}
                  className={`border-b border-[#F3F4F6] last:border-0 ${i === 0 ? 'cursor-pointer hover:bg-teal-50/40' : 'hover:bg-[#FAFAFA]/60'}`}
                >
                  <td className="px-5 py-4 text-[13px] font-body font-medium text-[#0A0A0A]">{f.name}</td>
                  <td className="px-5 py-4 text-[13px] font-body text-[#374151]">{f.type}</td>
                  <td className="px-5 py-4 text-[13px] font-body text-[#374151]">{f.location}</td>
                  <td className="px-5 py-4 text-[13px] font-body text-[#374151]">{f.localNumber}</td>
                  <td className="px-5 py-4">
                    <span className="inline-block px-2.5 py-0.5 rounded-md text-[11px] font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">{f.status}</span>
                  </td>
                  <td className="px-3 py-4"><MoreVertical size={14} className="text-[#9CA3AF]" /></td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </PortalShellV2>
  );
}
